import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ShoppingBag } from 'lucide-react'

import { SearchSelect } from '@/components/SearchSelect'
import { useNetwork } from '@/contexts/NetworkContext'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { OrdersApi } from '@/services/orders/ordersApi'

export interface OrderSelectProps {
  /** Order id, or '' for no selection. */
  value: string
  onChange: (orderId: string) => void
  /** Offers only this advertiser's orders. Omit to search every order in the network. */
  advertiserId?: string
  /** Label for the empty choice, offered first to clear the selection. */
  anyLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Searchable order picker, optionally narrowed to one advertiser. With
 * `anyLabel` it serves as a list filter, where '' means "not filtered".
 */
export function OrderSelect({ advertiserId, anyLabel, ...props }: OrderSelectProps) {
  const { t } = useTranslation()
  const { scopeKey } = useNetwork()
  const [term, setTerm] = useState('')
  const search = useDebouncedValue(term)

  // One advertiser's orders are few enough to list whole; the network's are
  // searched on the server as the user types.
  const { data: orders = [], isLoading, isFetching } = useQuery({
    queryKey: ['orders', 'picker', scopeKey, advertiserId ?? '', advertiserId ? '' : search],
    queryFn: async () =>
      advertiserId
        ? OrdersApi.getByAdvertiser(advertiserId)
        : (await OrdersApi.getPage(1, 50, true, search)).results,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })
  // The picked order may not be among the options — fetch it on its own to show its name.
  const listHasValue = !props.value || orders.some((o) => o.id === props.value)
  const { data: picked } = useQuery({
    queryKey: ['order', props.value],
    queryFn: () => OrdersApi.getById(props.value),
    enabled: !isLoading && !listHasValue,
    staleTime: 5 * 60 * 1000,
  })
  const options = useMemo(() => orders.map((o) => ({ value: o.id, label: o.name })), [orders])

  return (
    <SearchSelect
      {...props}
      options={options}
      placeholder={anyLabel ?? t('lineItems.selectOrder')}
      fallbackLabel={picked?.name}
      clearable={!!anyLabel}
      icon={ShoppingBag}
      loading={isLoading}
      onSearchChange={advertiserId ? undefined : setTerm}
      searching={isFetching && !isLoading}
      searchPlaceholder={t('lineItems.searchOrders')}
      emptyText={t('orders.noOrdersFound')}
    />
  )
}
